import { num, TYPE_LABEL, FAIL_LABEL, PROPOSAL_ORDER } from "@/lib/sk";

export const VIEW_KEY = (cid) => `sk.konfig.view.${cid}`;
export const VENTIL_FILTER_KEY = (cid) => `sk.ventiler.filter.${cid}`;

export const FIELD_LABEL = {
  type: "Ventiltype", dn: "DN", pn: "PN", dp_bar: "Maks ΔP (bar)", bto_nm: "BTO (Nm)", etc_nm: "ETC (Nm)", mast_nm: "MAST (Nm)", torque_nm: "Dreiemoment (Nm)", fail_mode: "Feilmodus", action: "Virkemåte", application: "Funksjon",
  feedback: "Fjernindikering", mech_indicator: "Mekanisk indikator", flange: "ISO 5211-flens", stem: "Spindel", closing_time_s: "Lukketid (s)", opening_time_s: "Åpnetid (s)", esd_group: "ESD/SSD-gruppe", valve_maker: "Ventilprodusent", valve_model: "Ventilmodell",
  valve_class: "Ventilklasse", pid: "P&ID", pipe_tag: "Rør", owner: "Eier", label: "Etikett", zone: "Sone", system: "System", remarks: "Merknad",
};
export const TYPE_OPTIONS = [["ball", "Kule"], ["butterfly", "Spjeld"], ["plug", "Plugg"], ["globe", "Globe"], ["gate", "Sluse"], ["check", "Tilbakeslag"], ["other", "Ukjent"]];
export const FAIL_OPTIONS = [["FC", "Fail close"], ["FO", "Fail open"], ["FL", "Fail last"]];
export const ACTION_OPTIONS = [["DA", "Dobbeltvirkende"], ["SR", "Fjærretur"]];
export const APPLICATION_OPTIONS = [["onoff", "Åpne/lukke"], ["throttling", "Regulering"]];
export const FEEDBACK_OPTIONS = [["switch", "Bryter (åpen/lukket)"], ["transmitter", "Kontinuerlig (4–20 mA)"], ["none", "Ingen"]];
export const BOOL_OPTIONS = [["true", "Ja"], ["false", "Nei"]];
export const FLANGE_OPTIONS = ["F04", "F05", "F07", "F10", "F12", "F14", "F16", "F25", "F30", "F35", "F40"].map((f) => [f, f]);
export const ACTION_LABEL = { DA: "Dobbeltvirkende", SR: "Fjærretur", LA: "Lineær" };
export const FEEDBACK_LABEL = { switch: "Bryter", transmitter: "Kontinuerlig", none: "Ingen" };
export const APPLICATION_LABEL = { onoff: "Åpne/lukke", throttling: "Regulering" };
export const ACTING_LABEL = { DA: "Dobbeltvirkende", SA: "Enkeltvirkende" };

/** Label for a cell as the grid shows it – normalised value in plain words, never the raw text (that lives in the inspector). */
export function cellText(field, v) {
  if (v === null || v === undefined || v === "") return "";
  if (field === "type") return TYPE_LABEL[v] || v;
  if (field === "fail_mode") return FAIL_LABEL[v] || v;
  if (field === "application") return APPLICATION_LABEL[v] || v;
  if (field === "feedback") return FEEDBACK_LABEL[v] || v;
  if (field === "action") return ACTION_LABEL[v] || v;
  if (field === "mech_indicator") return v === true || v === "true" ? "Ja" : "Nei";
  if (typeof v === "number") return num(v, Number.isInteger(v) ? 0 : 2);
  return String(v);
}

/** Filters on the valve list: the configurator's answer first (that is what the work is about), then data state and the person's own changes. */
export const VENTIL_FILTERS = [
  ["alle", "Alle"], ...PROPOSAL_ORDER.map((s) => [s, null]), ["valgt", "Eget valg"], ["avklares", "Data må avklares"], ["endret", "Endret av deg"],
];

export function matchValve(v, f, q) {
  const p = v.proposal || {};
  if (PROPOSAL_ORDER.includes(f) && p.status !== f) return false;
  if (f === "avklares" && v.status === "mapped") return false;
  if (f === "endret" && !Object.keys(v.overrides || {}).length) return false;
  if (f === "valgt" && !v.selection) return false;
  if (!q) return true;
  const s = q.toLowerCase();
  return [v.tag, cellText("type", v.type), v.valve_model, v.valve_maker, v.pid, v.system, v.zone, v.remarks, v.esd_group, p.code, p.family, v.flange, v.dn && `dn${v.dn}`, v.dn && `dn ${v.dn}`].some((x) => String(x || "").toLowerCase().includes(s));
}

export function filterCounts(valves) {
  const out = { alle: valves.length, avklares: valves.filter((v) => v.status !== "mapped").length, endret: valves.filter((v) => Object.keys(v.overrides || {}).length).length, valgt: valves.filter((v) => v.selection).length };
  PROPOSAL_ORDER.forEach((s) => { out[s] = valves.filter((v) => v.proposal?.status === s).length; });
  return out;
}

export const RULE_LABEL = {
  actuator_sizing: "Aktuatorvalg per ventil", flange_adapter: "Flensadapter", svc_by_zone: "Ett skap per sone", one_module_per_valve: "Én modul per ventil", handpump_per_3_cabinets: "Håndpumpe per tre skap",
  hpu_sizing: "HPU-dimensjonering", extra_accumulator: "Tilleggsakkumulator", one_control_station: "Én kontrollstasjon", operator_panels: "Operatørpaneler", panel_per_cabinet: "Panel per skap", io_per_8_valves: "I/O per 8 ventiler",
  bus_cable_estimate: "Buskabel – estimat", ias_gateway: "IAS-gateway (valgt)", switch_per_valve: "Indikator per ventil", transmitter_when_required: "Transmitter der det kreves", tube_per_valve: "Rør per ventil", hose_kit_per_actuator: "Slangesett per aktuator",
  fitting_kit_per_valve: "Koblingssett per ventil", engineering_hours: "Engineering-timer", doc_package_by_size: "Dokumentasjonspakke etter størrelse", fat_days: "FAT-dager", commissioning_days: "Igangkjøringsdager", travel_estimate: "Reiser – estimat", class_approval: "Klassegodkjenning",
  training: "Opplæring", spares_pct: "Reservedeler, % av maskinvare", freight_pct: "Frakt, % av maskinvare",
};
export const ruleLabel = (r) => RULE_LABEL[r] || r || "";

export const CONFIG_STATUS = {
  validated: { label: "Teknisk validert", cls: "v-ok", lead: "Alle komponenter er valgt etter oppgitte data." },
  exception: { label: "Godkjent med dokumenterte unntak", cls: "v-ok", lead: "Minst én ventil er lukket ved manuell verifikasjon eller akseptert kravavvik – grunnlaget står som forutsetning i kalkylen." },
  assumed: { label: "Antakelser må bekreftes", cls: "v-assumed", lead: "Noen komponenter bygger på estimater – de står som forutsetninger i kalkylen." },
  needs_engineer: { label: "Krever ingeniørvurdering", cls: "v-eng", lead: "Minst én ventil eller aggregatet må vurderes av ingeniør før dette kan tilbys." },
  blocked: { label: "Ventiler mangler data", cls: "v-blocked", lead: "Noen ventiler kan ikke dimensjoneres før ventillisten er avklart." },
};

export const PRICE_BASIS = {
  bekreftet: { label: "Bekreftet pris", cls: "text-foreground" },
  demo: { label: "Demo-referanse – ikke bekreftet", cls: "text-[hsl(var(--v-assumed))]" },
  ukjent: { label: "Pris ukjent", cls: "text-muted-foreground" },
  avledet: { label: "Avledet av maskinvare", cls: "text-muted-foreground" },
};

export const nm = (n) => (n == null ? "–" : `${num(n)} Nm`);

/** Three figures that are deliberately not one: known component cost · the configuration's known list price · the calculation's sum. Unknown stays «–». */
export function levels(active, totals) {
  const t = active?.totals || {};
  const unknown = active?.unknown_price_lines || 0;
  const demo = active?.demo_lines || 0;
  const hint = unknown ? `${unknown} ${unknown === 1 ? "linje" : "linjer"} uten pris er ikke med` : demo ? `${demo} demo-referanser – ikke bekreftet` : "kjente komponentpriser";
  const kalk = totals || {};
  return [
    { key: "kost", label: "Teknisk kost", value: t.cost, hint, preliminary: unknown > 0 || demo > 0 },
    { key: "liste", label: "Listepris", value: t.price, hint: unknown ? `${unknown} uten pris – utenfor summen` : `${t.lines || 0} linjer`, preliminary: unknown > 0 || demo > 0 },
    { key: "kalkyle", label: "Kalkylen", value: kalk.sum_kind === "none" ? null : kalk.price, hint: kalk.sum_kind === "complete" ? "komplett fastprissum" : kalk.sum_kind === "preliminary" ? "foreløpig – poster mangler pris" : active?.synced ? "lagt inn – ingen linjer har pris ennå" : "ikke lagt inn ennå", preliminary: kalk.sum_kind === "preliminary", db: kalk.db_pct },
  ];
}

export function componentByKey(active, key) {
  for (const g of active?.groups_full || []) {
    const it = (g.items || []).find((x) => x.component_key === key);
    if (it) return { comp: it, group: g };
  }
  return null;
}

/** What the person should do next with the configuration, as one button. */
export function primaryAction(view) {
  const a = view?.active;
  if (!view || !view.stats?.total) return { key: "upload", label: "Last opp ventilliste" };
  if (view.params?.project_pressure_bar == null) return { key: "pressure", label: "Bekreft prosjekttrykk" };
  if (!a) return { key: "run", label: "Konfigurer løsning" };
  if (a.outdated?.length) return { key: "rerun", label: "Kjør konfigurasjonen på nytt" };
  if ((view.sync && !view.sync.in_sync) || !a.synced) return { key: "sync", label: "Legg i kalkylen" };
  return { key: "kalkyle", label: "Gå til kalkylen" };
}

export const goto = (detail) => window.dispatchEvent(new CustomEvent("canvas:goto", { detail }));
